import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Page, Widget, PageTheme } from '@/types';

const COLLECTION_NAME = 'pages';

export const defaultTheme: PageTheme = {
  backgroundColor: '#f5f5f5',
  textColor: '#1a1a1a',
  accentColor: '#D4E157',
  fontFamily: 'Inter',
};

export interface CreatePageInput {
  userId: string;
  username: string;
  title?: string;
  bio?: string;
  avatarUrl?: string;
}

export interface UpdatePageInput {
  title?: string;
  bio?: string;
  avatarUrl?: string;
  theme?: Partial<PageTheme>;
  widgets?: Widget[];
}

// Create or get user's page
export const createPage = async (input: CreatePageInput): Promise<Page> => {
  const { userId, username, title, bio, avatarUrl } = input;
  const normalizedUsername = username.toLowerCase();

  // Check if page already exists
  const existingPage = await getPageByUsername(normalizedUsername);
  if (existingPage) {
    return existingPage;
  }

  const pageData = {
    userId,
    username: normalizedUsername,
    title: title || username,
    bio: bio || '',
    avatarUrl: avatarUrl || null,
    theme: defaultTheme,
    widgets: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = doc(collection(db, COLLECTION_NAME));
  await setDoc(docRef, { id: docRef.id, ...pageData });

  const docSnap = await getDoc(docRef);
  return { id: docRef.id, ...docSnap.data() } as Page;
};

// Get page by username
export const getPageByUsername = async (username: string): Promise<Page | null> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('username', '==', username.toLowerCase())
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as Page;
};

// Get page by user ID
export const getPageByUserId = async (userId: string): Promise<Page | null> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as Page;
};

// Update page
export const updatePage = async (
  pageId: string,
  input: UpdatePageInput
): Promise<Page> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Page not found');
  }

  const currentData = docSnap.data() as Page;

  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (input.title !== undefined) updateData.title = input.title;
  if (input.bio !== undefined) updateData.bio = input.bio;
  if (input.avatarUrl !== undefined) updateData.avatarUrl = input.avatarUrl;
  if (input.widgets !== undefined) updateData.widgets = input.widgets;

  if (input.theme) {
    updateData.theme = { ...currentData.theme, ...input.theme };
  }

  await setDoc(docRef, updateData, { merge: true });

  const updatedSnap = await getDoc(docRef);
  return { id: updatedSnap.id, ...updatedSnap.data() } as Page;
};

// Add widget to page
export const addWidget = async (
  pageId: string,
  widget: Omit<Widget, 'id' | 'order'>
): Promise<Page> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Page not found');
  }

  const currentData = docSnap.data() as Page;
  const widgets = currentData.widgets || [];

  const newWidget: Widget = {
    ...widget,
    id: `widget_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    order: widgets.length,
  };

  const updatedWidgets = [...widgets, newWidget];

  await setDoc(docRef, { widgets: updatedWidgets, updatedAt: serverTimestamp() }, { merge: true });

  return { ...currentData, widgets: updatedWidgets };
};

// Update widget
export const updateWidget = async (
  pageId: string,
  widgetId: string,
  data: Partial<Widget>
): Promise<Page> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Page not found');
  }

  const currentData = docSnap.data() as Page;
  const widgets = currentData.widgets || [];

  const updatedWidgets = widgets.map((w) =>
    w.id === widgetId ? { ...w, ...data } : w
  );

  await setDoc(docRef, { widgets: updatedWidgets, updatedAt: serverTimestamp() }, { merge: true });

  return { ...currentData, widgets: updatedWidgets };
};

// Delete widget
export const deleteWidget = async (pageId: string, widgetId: string): Promise<Page> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Page not found');
  }

  const currentData = docSnap.data() as Page;
  const widgets = currentData.widgets || [];

  const updatedWidgets = widgets
    .filter((w) => w.id !== widgetId)
    .map((w, index) => ({ ...w, order: index }));

  await setDoc(docRef, { widgets: updatedWidgets, updatedAt: serverTimestamp() }, { merge: true });

  return { ...currentData, widgets: updatedWidgets };
};

// Reorder widgets
export const reorderWidgets = async (
  pageId: string,
  widgetIds: string[]
): Promise<Page> => {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Page not found');
  }

  const currentData = docSnap.data() as Page;
  const widgetsMap = new Map(currentData.widgets.map((w) => [w.id, w]));

  const updatedWidgets = widgetIds
    .map((id, index) => {
      const widget = widgetsMap.get(id);
      return widget ? { ...widget, order: index } : null;
    })
    .filter((w): w is Widget => w !== null);

  await setDoc(docRef, { widgets: updatedWidgets, updatedAt: serverTimestamp() }, { merge: true });

  return { ...currentData, widgets: updatedWidgets };
};

// Update page username
export const updatePageUsername = async (
  userId: string,
  newUsername: string
): Promise<void> => {
  const page = await getPageByUserId(userId);

  if (!page) {
    throw new Error('Page not found');
  }

  const docRef = doc(db, COLLECTION_NAME, page.id);
  await setDoc(docRef, {
    username: newUsername.toLowerCase(),
    updatedAt: serverTimestamp()
  }, { merge: true });
};
